'use strict';

import React from 'react';
import PropTypes from 'prop-types';
import { Link, withRouter } from 'react-router-dom';
import { Container, Row, Col, ButtonToolbar, Button, DropdownButton, Dropdown, Table } from 'react-bootstrap';
import backend from '../backend';
import util from '../util';
import _ from 'underscore';

import BetaTag from "./BetaTag";


// Sort first by year, then by PMID (as a proxy for a more specific date of publication?)
const pubsOrdering = function(pub1, pub2) {
    if (isNaN(pub1.year)) {
        return 1;
    }
    if (isNaN(pub2.year)) {
        return -1;
    }
    if (pub2.year - pub1.year !== 0) {
        return pub2.year - pub1.year;
    }
    if (pub2.pmid - pub1.pmid !== 0) {
        return pub2.pmid - pub1.pmid;
    }
    return 1;
};


function formatMatches(matches, count) {
    let ms = matches.slice(0, count);

    return ms.map((match, i) =>
        // we extract entries like <<<some text here>>>, keeping the first < as an indicator that the text is highlighted
        (<li key={i}>
            <small>...
            {
                _.unescape(match).split(/<<(<.*)>>>/g).map((x, j) =>
                    x.startsWith('<')
                        ? <span key={j} style={{backgroundColor: 'yellow', borderRadius: 2, border: 'solid 1px #bca723'}}>{x.slice(1)}</span>
                        : <React.Fragment key={j}>{x}</React.Fragment>)
            }
            ...</small>
        </li>)
    );

    // return ms.map(match => <div><small>... {_.unescape(match)} ...</small></div>);
}

/**
 * Given a string of authors, returns an object with two lists: a full list of parsed-out authors and an abbreviated
 * list of authors. If the number of authors is less than maxBeforeCut, the abbreviated list will be the same as the
 * full list. If it's more, the abbreviated list will contain a maximum of maxAfterCut authors.
 * @param authors the list of semicolon-delimited authors, with each author having a comma-delimited given and first name(s).
 * @param maxBeforeCut the number of authors which triggers the abbreviated list to be shortened
 * @param maxAfterCut the maximum number of authors in the resulting abbreviated list
 * @param abbrevFirstNames whether first name(s) should be shortened to just their first characters, e.g. "Emily Leigh" -> EL
 * @return {{full: *, abbreviated: *, diff: number}}
 */
export function authorsList(authors, maxBeforeCut, maxAfterCut, abbrevFirstNames = false) {
    if (!maxAfterCut) {
        maxAfterCut = maxBeforeCut;
    }

    const authList = authors.split(";").map(x => {
        // for an author like "Rauscher, Emily A" return "Rauscher EA"
        const [given, rest] = x.trim().split(",", 2);

        if (!rest) {
            // sometimes we encounter names of orgs or consortiums, e.g., "ENIGMA"
            // there's no second part there, so we just return the first (aka 'given name') part
            return given.trim();
        }

        if (abbrevFirstNames) {
            return `${given.trim()} ${rest.trim().split(" ").map(x => x.slice(0, 1)).join("")}`;
        }
        else {
            return `${given.trim()}, ${rest.trim()}`;
        }

    });

    const abbrevList = authList.length > maxBeforeCut && authList.length > maxAfterCut
        ? authList.slice(0, maxAfterCut)
        : authList;

    return {
        full: authList,
        abbreviated: abbrevList,
        diff: authList.length - abbrevList.length // if nonzero, indicates caller should add 'et al'
    };
}


class AuthorList extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            expanded: false
        };
    }

    render() {
        const {authors, maxCount} = this.props;
        const authList = authorsList(authors, maxCount); // authors.split(";");

        return (<div style={{marginTop: '5px'}}>
            {
                this.state.expanded
                    ? `${authList.full.join("; ")}.`
                    : (
                        <span>
                            {authList.abbreviated.join("; ")}
                            ...&nbsp;
                            <a style={{cursor: 'pointer'}} onClick={() => this.setState({ expanded: true})}>
                                {`(${authList.diff} more)`}
                            </a>
                        </span>
                    )
            }
        </div>);
    }
}

AuthorList.propTypes = {
    authors: PropTypes.string,
    maxCount: PropTypes.number
};

class LiteratureTable extends React.Component {
    constructor(props) {
        super(props);
        this.state = {};
	this.clipboardRef = React.createRef();
	this.subscriptions = [];
    }

    componentDidMount() {
        const routeId =
            (this.props.match && this.props.match.params && this.props.match.params.id) ||
            (this.props.params && this.props.params.id);

        if (!this.props.variant && routeId) {
            const sub = backend.variant(routeId).subscribe(
	    resp => {
                    return this.setState({data: resp.data, error: null});
                },
                () => { this.setState({error: 'Problem connecting to server'}); }
            );
	    this.subscriptions.push(sub);
        }

	const papersId = this.props.variant ? this.props.variant.id : routeId;
	if (papersId) {
	    const sub = backend.variantPapers(papersId).subscribe(
            resp => {
                this.setState({papers: resp.data, paperError: null});
            },
            () => { this.setState({paperError: 'Problem retrieving papers'}); }
            );
	    this.subscriptions.push(sub);
	}
    }

    componentWillUnmount() {
        if (this.subscriptions) {
            this.subscriptions.forEach(s => {
                if (s && typeof s.unsubscribe === 'function') s.unsubscribe();
            });
        }
    }

    toTSV() {
        const headerRow = ["title", "authors", "journal", "year", "keywords", "pmid"].join("\t");
        return this.state.papers
            ? [headerRow].concat(
                this.state.papers.map(({title, authors, journal, year, keywords, pmid}) =>
                    [title, authors, journal, year, keywords, pmid].join("\t")
                )
              ).join("\n")
            : "";
    }

    toCitation() {
        return this.state.papers
            ? this.state.papers.map(({title, authors, journal, year, keywords, pmid}) => {
                const authList = authorsList(authors, 5, 3, true);
                let abbrevList = authList.abbreviated;
                if (authList.diff > 0) {
                    abbrevList.push("et al");
                }

                return `${abbrevList.join(", ")}. (${year}) ${title} ${journal}\nPMID: ${pmid}\nKeywords: ${keywords}\n\n`;
            })
            : "";
    }

    toJSON() {
        return JSON.stringify(this.state.papers ? this.state.papers : []);
    }

    copyTable() {
        const textarea = this.clipboardRef.current;
	if (!textarea) return;
        textarea.value = this.toCitation();
        textarea.select();
        document.execCommand('copy');
    }

    trackLitAccess(elem) {
        const targetURL = elem.target.getAttribute('href');

        if (window.ga) {
            window.ga('send', 'event', {
                eventCategory: 'Literature Outbound Link',
                eventAction: 'click',
                eventLabel: targetURL
            });
        }
    }

    getRelevantDataFromSynonyms(synonyms) {
        let outputSyns = new Set();
        _.each(synonyms.split(','), function(syn) {
            if(!(/^\d+$/.test(syn)) && syn.match(/^\d/)) {
                // keep synonyms that start with a number but aren't all numbers
                outputSyns.add(syn);
            } else if (syn.includes('c.')) {
                outputSyns.add(syn.split('c.')[1]);
            } else if (syn.includes('p.')) {
                outputSyns.add(syn.split('p.')[1]);
            }
        });
        return outputSyns;
    }

    getProteinChange(pc) {
        if (util.isEmptyField(pc) || pc === "p.?") {
            return '';
        }
        // remove leading p.
        pc = pc.replace('p.', '');
        // remove parentheses
        pc = pc.replace(/[()]/g, '');
        return pc;
    }

    getCDNA(cDNA) {
        if (util.isEmptyField(cDNA)) {
            return '';
        }
        // remove refseq and c.
        cDNA = cDNA.split(':')[1].replace('c.', '');
        return cDNA;
    }

    getSearchTerm() {
        const variant = this.props.variant || (this.state.data && this.state.data[0]);
	if (!variant) return '';
	let geneSymbol = variant.Gene_Symbol;
        let terms = new Set();
        terms.add(this.getProteinChange(variant.Protein_Change));
        terms.add(this.getCDNA(variant.HGVS_cDNA));
        terms = new Set([...terms, ...this.getRelevantDataFromSynonyms(variant.Synonyms)]);
        let searchTerm = `${geneSymbol}+AND+("${[...terms].join('"+OR+"')}")`;
        return searchTerm;
    }

    render() {
        if (!this.props.variant && !this.state.data) {
            return (<div />);
        }
	const variant = this.props.variant || (this.state.data && this.state.data[0]);

        // if no lit results exist, return a 'no lit results' placeholder
        let litResultsExist = false;
        let litRows = (
            <tr>
                <td colSpan={2} style={{textAlign: 'center'}}><i>No literature results for this variant.</i></td>
            </tr>
        );

        if (this.state.papers && this.state.papers.length > 0) {
            litResultsExist = true;

            litRows = this.state.papers.sort(pubsOrdering);

            if (this.props.maxRows) {
                litRows = litRows.slice(0, this.props.maxRows);
            }

            litRows = litRows.map(({title, authors, journal, year, mentions, pmid}) => (
                <tr key={pmid}>
                    <td>
                        <b style={{fontSize: '16px'}}>{title}</b>

                        <AuthorList authors={authors} maxCount={3} />

                        <div className="lit-text-matches">
                            <b>Text Matches:</b>
                            {
                                mentions.length ? (
                                    <ol>
                                        {formatMatches(mentions, 3)}
                                    </ol>
                                ) : null
                            }
                        </div>
                    </td>
                    <td style={{width: '12%'}}>
                        <div className="pmid">PMID: <a onClick={this.trackLitAccess} href={`https://www.ncbi.nlm.nih.gov/pubmed/${pmid}`} target='_blank' rel="noopener noreferrer">{pmid}</a></div>
                        <div>{year}</div>
                        <div>{journal}</div>
                    </td>
                </tr>
            ));
        }

        let toTSVURL = `data:text/tab-separated-values;charset=utf-8,${encodeURIComponent(this.toTSV())}`;
        let toJSONURL = `data:text/json;charset=utf-8,${encodeURIComponent(this.toJSON())}`;

        let googleSearchLink = `https://www.google.com/search?q=${this.getSearchTerm()}`;
        let pubmedSearchLink = `https://pubmed.ncbi.nlm.nih.gov/?term=${this.getSearchTerm()}`;

        let crawlDate = this.state.papers && this.state.papers.length > 0 && util.normalizeDateFieldDisplay(this.state.papers[0].crawl_date);
        let component = (
            (litResultsExist || !this.props.hideEmptyItems) &&
            <div>
                <h4>Literature Search Results{crawlDate && ` (as of ${crawlDate})`}: <BetaTag margin="0.25em" verticalALign="top" hoverText="Literature Search is a beta feature, so please beware of erroneous or missing results. You are welcome to contact us about observed errors." /></h4>
                    <Table className='nopointer literature-rows' bordered>
                        <thead>
                            <tr className="table-active">
                                <th>Publications Mentioning this Variant</th>
                                <th style={{width: '12%'}}>Citation Info</th>
                            </tr>
                        </thead>
                        <tbody>
                        {litRows}
                        </tbody>
                    </Table>

                    { this.state.papers && this.state.papers.length > this.props.maxRows && variant
                        ? ( <div style={{textAlign: "center", marginBottom: '1em'}}>
                                <Link to={`/variant_literature/${variant.id}`}>
                                    View {`${this.state.papers.length - this.props.maxRows}`} more publications...
                                </Link>
                            </div>
                        ) : null
                    }

                    <div>
                        <em className="float-start" style={{marginBottom: '1em'}}>
                            To report a false positive, or to include a paper that should be in the list, please <a href="mailto:brca-exchange-contact@genomicsandhealth.org">contact us</a>.
                            <br />
                            To search for additional content, click here to search <a href={googleSearchLink}>Google</a> or <a href={pubmedSearchLink}>PubMed</a>.
                             These searches can have a high false positive rate, but can also find additional content that was missed in the pre-computed searches.
                        </em>

                        <ButtonToolbar className='float-end'>
                            <Button onClick={this.copyTable.bind(this)}>Copy To Clipboard</Button>
                            <DropdownButton id="download_table_dropdown" title="Export" align="end">
                                <Dropdown.Item as="a" href={toTSVURL} download="variant-literature.tsv">Excel (.tsv format)</Dropdown.Item>
                                <Dropdown.Item as="a" href={toJSONURL} download="variant-literature.json">JSON</Dropdown.Item>
			    </DropdownButton>
                        </ButtonToolbar>
                    </div>

                <textarea ref={this.clipboardRef} style={{padding: '0', width: '0', height: '0', marginLeft: '-99999999px' }}/>
            </div>
        );

        if (this.props.variant) {
            return component;
        } else {
            return (
                <Container>
                    <Row>
                        <Col md={12} className="variant-literature-col">
                            <h3>{this.state.data[0]["HGVS_cDNA"]}</h3>
                            {component}
                        </Col>
                    </Row>
                </Container>
            );
        }
    }
}

export default withRouter(LiteratureTable);
